<script setup>
import { computed, watch, ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  endpointId: { type: String, default: 'create' },
  fieldDefinitions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'update:payload',
  'update:pathParams',
  'update:queryParams',
]);
const { t } = useI18n();
const enabled = ref({});
const values = ref({});
const enabledCustom = ref({});
const customValues = ref({});

const HUMAN_ATTENTION_CUSTOM_FIELDS = [
  'nome_completo',
  'cpf',
  'nome_do_pai',
  'nome_da_mae',
  'data_de_nascimento',
  'cep',
  'e_mail',
  'como_voce_nos_conheceu',
  'convenio_ou_particular',
  'carteirinha_do_convenio',
  'pedido_medico',
];

const allFields = computed(() => {
  const common = [
    'phone_number',
    'starts_at',
    'duration_minutes',
    'title',
    'description',
    'note',
  ];
  const map = {
    create: [...common],
    list: ['date', 'from', 'to', 'status', 'include_archived'],
    list_upcoming: ['from', 'to', 'status'],
    confirm: ['id', 'note'],
    update: [
      'id',
      'status',
      'starts_at',
      'duration_minutes',
      'phone_number',
      'archived',
      'note',
    ],
    destroy: ['id', 'note'],
    create_human_attention: ['phone_number'],
  };
  return map[props.endpointId] || [];
});

const activeFieldDefs = computed(() =>
  props.fieldDefinitions.filter(f => !f.archived_at)
);

const showCustomFields = computed(() =>
  ['create', 'update'].includes(props.endpointId)
);

const showHumanAttentionFields = computed(
  () => props.endpointId === 'create_human_attention'
);

const buildOutputs = () => {
  const active = allFields.value.filter(f => enabled.value[f]);
  const isListEndpoint = props.endpointId.startsWith('list');

  const pathP = {};
  if (active.includes('id')) {
    pathP.id = values.value.id;
  }

  const queryP = {};
  if (isListEndpoint) {
    active
      .filter(f => f !== 'id')
      .forEach(f => {
        queryP[f] = values.value[f] ?? '';
      });
  }
  if (props.endpointId === 'list_upcoming') {
    if (!queryP.from) queryP.from = 'now';
    if (!queryP.to) queryP.to = '+24h';
    if (!queryP.status) queryP.status = 'confirmed';
  }

  const body = {};
  if (!isListEndpoint) {
    active
      .filter(f => f !== 'id')
      .forEach(f => {
        body[f] = values.value[f] ?? '';
      });

    const activeCustom = activeFieldDefs.value.filter(
      f => enabledCustom.value[f.key]
    );
    if (activeCustom.length > 0) {
      body.custom_fields = {};
      activeCustom.forEach(f => {
        body.custom_fields[f.key] = customValues.value[f.key] ?? '';
      });
    }
  }

  if (showHumanAttentionFields.value) {
    HUMAN_ATTENTION_CUSTOM_FIELDS.forEach(f => {
      if (enabledCustom.value[f] && customValues.value[f] !== undefined) {
        body[f] = customValues.value[f];
      }
    });
  }

  emit('update:payload', body);
  emit('update:pathParams', pathP);
  emit('update:queryParams', queryP);
};

watch(
  [enabled, values, enabledCustom, customValues, () => props.endpointId],
  buildOutputs,
  { deep: true }
);
</script>

<template>
  <div class="flex flex-col gap-2">
    <div
      v-for="f in allFields"
      :key="f"
      class="flex flex-wrap items-center gap-2"
    >
      <input v-model="enabled[f]" type="checkbox" />
      <label class="w-32 shrink-0 text-sm text-n-slate-11">{{ f }}</label>
      <input
        v-model="values[f]"
        class="min-w-0 flex-1 rounded border border-n-slate-5 px-2 py-1 text-sm"
      />
    </div>

    <template v-if="showHumanAttentionFields">
      <p
        class="mt-3 text-xs font-semibold uppercase tracking-wide text-n-slate-9"
      >
        {{ t('PAINEL.API_PLAYGROUND.OPTIONAL_FIELDS') }}
      </p>
      <div
        v-for="f in HUMAN_ATTENTION_CUSTOM_FIELDS"
        :key="f"
        class="flex flex-wrap items-center gap-2"
      >
        <input v-model="enabledCustom[f]" type="checkbox" />
        <label class="w-32 shrink-0 text-sm text-n-slate-11">{{ f }}</label>
        <input
          v-model="customValues[f]"
          :placeholder="f"
          class="min-w-0 flex-1 rounded border border-n-slate-5 px-2 py-1 text-sm"
        />
      </div>
    </template>

    <template v-if="showCustomFields && activeFieldDefs.length > 0">
      <p
        class="mt-3 text-xs font-semibold uppercase tracking-wide text-n-slate-9"
      >
        {{ t('PAINEL.API_PLAYGROUND.CUSTOM_FIELDS_LABEL') }}
      </p>
      <div
        v-for="fd in activeFieldDefs"
        :key="fd.key"
        class="flex flex-wrap items-center gap-2"
      >
        <input v-model="enabledCustom[fd.key]" type="checkbox" />
        <label class="w-32 shrink-0 text-sm text-n-slate-11" :title="fd.key">
          {{ fd.label }}
        </label>
        <input
          v-model="customValues[fd.key]"
          :placeholder="fd.placeholder || fd.key"
          class="min-w-0 flex-1 rounded border border-n-slate-5 px-2 py-1 text-sm"
        />
      </div>
    </template>
  </div>
</template>
